import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loading } from '../../components/office';
import { purchaseOrdersApi, stockApi, suppliersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { orderStatus, qty, statusNote, theirs as whose } from '../../lib/orders';
import ConfirmOrderDialog from './ConfirmOrderDialog';
import OrderEmailField, { looksLikeEmail } from './OrderEmailField';

// One order (Back office sweep, Oct 2026: D10, mock "Draft order"). A draft is checked here,
// changes saving as you go: quantities in whole packs, prices, the delivery date and a note,
// with the total. Then it's sent: emailed to the supplier as a PDF, or marked sent when it
// went another way. A sent order is marked confirmed when they reply (what they'll send),
// chased when they haven't, and received from here; a received one shows what came.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const day = (iso) => (iso ? new Date(iso.length > 10 ? iso : `${iso}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) : '');

export default function OrderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [stock, setStock] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [saved, setSaved] = useState(true);
  const [sending, setSending] = useState(false);
  const [cantEmail, setCantEmail] = useState(null);   // why it can't be emailed, while offering the other way
  const [bundleName, setBundleName] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [chasing, setChasing] = useState(false);   // one chase at a time: it emails them
  const [emailTo, setEmailTo] = useState('');   // an address typed for a supplier with none
  const [keep, setKeep] = useState(true);       // …kept on their account, or used this once
  const timer = useRef(null);
  const queue = useRef(Promise.resolve(true));  // saves go one at a time, in order
  const latest = useRef(0);                     // the newest save asked for

  useEffect(() => {
    Promise.all([purchaseOrdersApi.get(id), stockApi.getAll(), suppliersApi.getAll().catch(() => ({ data: [] }))])
      .then(([o, s, sup]) => { setOrder(o.data); setStock(s.data || []); setSupplier((sup.data || []).find((x) => x.id === o.data.supplier_id) || null); })
      .catch(() => toast.error("Couldn't load the order"));
  }, [id]);

  const byId = useMemo(() => Object.fromEntries(stock.map((s) => [s.id, s])), [stock]);
  // This site's shelves only: someone over several sites sees every site's stock.
  const theirs = stock.filter((s) => s.supplier_id === order?.supplier_id && (!s.site_id || s.site_id === order?.site_id)
    && !(order?.lines || []).some((l) => l.stock_id === s.id));

  // A draft saves itself a moment after each change.
  const change = (patch) => {
    const next = { ...order, ...patch };
    next.total = Math.round(next.lines.reduce((t, l) => t + l.quantity * l.price, 0) * 100) / 100;
    setOrder(next);
    setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save(next), 700);
  };
  // Each save waits for the one before, so an older draft can't land after a newer one; only
  // the newest says Saved.
  const save = (o = order) => {
    const n = ++latest.current;
    const run = queue.current.then(async () => {
      try {
        const { data } = await purchaseOrdersApi.update(o.id, { lines: o.lines.map((l) => ({ stock_id: l.stock_id, quantity: l.quantity, price: l.price })), deliver_on: o.deliver_on || null, note: o.note || '' });
        if (n === latest.current) { setOrder((cur) => ({ ...cur, total: data.total })); setSaved(true); }
        return true;
      } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the order"); return false; }
    });
    queue.current = run;
    return run;
  };
  const setLine = (sid, patch) => change({ lines: order.lines.map((l) => (l.stock_id === sid ? { ...l, ...patch } : l)) });
  // One pack more or less; down to nothing takes the line off (Martin, 27 Oct). "+ Add from"
  // puts it back.
  const step = (l, dir) => {
    const pack = Number(l.pack_size) || 1;
    const next = Math.round((l.quantity + dir * pack) * 1000) / 1000;
    if (next > 0) setLine(l.stock_id, { quantity: next });
    else change({ lines: order.lines.filter((x) => x.stock_id !== l.stock_id) });
  };
  const addLine = (sid) => {
    const s = byId[sid];
    if (!s) return;
    change({ lines: [...order.lines, { stock_id: s.id, name: s.name, unit: s.unit, quantity: Number(s.pack_size) || 1, price: s.cost_per_unit || 0, pack_size: Number(s.pack_size) || 1, order_code: s.order_code || '' }] });
  };

  const send = async () => {
    clearTimeout(timer.current);
    if (!saved && !(await save())) return;
    const typed = !supplier?.email && emailTo.trim();
    if (typed && !looksLikeEmail(typed)) { toast.error("That doesn't look like an email address"); return; }
    setSending(true);
    try {
      if (typed && keep) {
        await suppliersApi.patch(supplier.id, { email: typed });
        setSupplier((s) => ({ ...s, email: typed }));
      }
      const { data } = await purchaseOrdersApi.send(order.id, typed && !keep ? typed : undefined);
      setOrder(data);
      toast.success(`Sent to ${data.sent_to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'cant_email') setCantEmail(d.message);
      else toast.error((typeof d === 'string' && d) || "Couldn't send it");
    } finally { setSending(false); }
  };
  const markSent = async () => {
    try {
      const { data } = await purchaseOrdersApi.markSent(order.id);
      setOrder(data); setCantEmail(null);
      toast.success('Marked as sent, not emailed', { action: { label: 'Undo', onClick: async () => {
        try { const { data: back } = await purchaseOrdersApi.unsend(order.id); setOrder(back); toast.success(`${order.number} is a draft again`); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't undo it"); }
      } } });
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't mark it sent"); }
  };
  const downloadPdf = async () => {
    try {
      const { data } = await purchaseOrdersApi.pdf(order.id);
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${order.number}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Couldn't make the PDF"); }
  };
  const remove = async () => {
    try { await purchaseOrdersApi.remove(order.id); toast.success('Draft deleted'); navigate('/back-office/orders'); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); }
  };
  const chase = async () => {
    if (chasing) return;
    setChasing(true);
    try { const { data } = await purchaseOrdersApi.chase(order.id); setOrder(data); toast.success(`Chased: emailed to ${order.supplier_name} again`); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'cant_email') toast.error(`${d.message} ${d.phone ? `Ring them on ${d.phone}.` : 'Ring them.'}`);
      else toast.error((typeof d === 'string' && d) || "Couldn't chase it");
    } finally { setChasing(false); }
  };
  const saveBundle = async () => {
    try {
      await purchaseOrdersApi.saveBundle(order.id, bundleName.trim());
      // It's kept on Bundles (and Orders' Your bundles): say where, with a way there.
      toast.success(`Saved as ${bundleName.trim()}, under Bundles`, { action: { label: 'See bundles', onClick: () => navigate('/back-office/orders/bundles') } });
      setBundleName(null);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the bundle"); }
  };

  if (!order) return <Loading />;
  const draft = order.status === 'draft';
  const st = orderStatus(order);
  const came = Object.fromEntries((order.received_lines || []).map((l) => [l.stock_id, l]));
  const said = order.status === 'sent' && order.confirmed_at;

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="order-page">
      <Link to="/back-office/orders" className="text-[13px] text-ink-soft hover:text-ink">‹ Orders</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-heading text-[26px] font-extrabold text-ink">{order.supplier_name}</h1>
            <Signal tone={st.tone} dot={false}>{st.words}</Signal>
          </div>
          <p className="text-[14px] text-ink-soft">
            Order <b className="font-heading font-bold text-ink">{order.number}</b>
            {draft ? ' · check it, then send it; the number goes on the email and PDF.' : ''}
            {order.status === 'sent' && ` · sent ${day(order.sent_at)}${order.sent_to ? ` to ${order.sent_to}` : ''}${order.deliver_on ? `, due ${day(order.deliver_on)}` : ''}.`}
            {order.received_at && ` · received ${day(order.received_at)} by ${order.received_by}${order.their_ref ? `, their ref ${order.their_ref}` : ''}.`}
          </p>
          {order.status === 'sent' && <p className="text-[13px] text-ink-faint" data-testid="order-stage-note">{statusNote(order)}{order.stage === 'chase' && order.supplier_phone ? ` · ${order.supplier_phone}` : ''}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBundleName(`${order.supplier_name}, weekly`)} data-testid="order-save-bundle">Save as a bundle</Button>
          {!draft && <Button variant="outline" onClick={downloadPdf} data-testid="order-pdf">Download PDF</Button>}
          {order.stage === 'chase' && <Button variant="outline" onClick={chase} disabled={chasing} data-testid="order-chase">{chasing ? 'Chasing…' : 'Chase'}</Button>}
          {order.status === 'sent' && <Button variant="outline" onClick={() => setConfirming(true)} data-testid="order-confirm">{said ? 'Change what they said' : 'Mark confirmed'}</Button>}
          {order.status === 'sent' && <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate(`/back-office/orders/${order.id}/receive`)} data-testid="order-receive">Receive it</Button>}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-hairline bg-white">
        <table className="w-full min-w-[640px] text-[14px]">
          <thead><tr className="border-b border-hairline bg-surface-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            <th className="px-4 py-2">Item</th><th className="px-3 py-2">{order.received_at || said ? 'Ordered' : 'Quantity'}</th>{said && <th className="px-3 py-2">They&apos;ll send</th>}{order.received_at && <th className="px-3 py-2">Came</th>}<th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Total</th><th className="w-8" />
          </tr></thead>
          <tbody>
            {order.lines.map((l) => {
              const s = byId[l.stock_id];
              const c = came[l.stock_id];
              return (
                <tr key={l.stock_id} className="border-b border-hairline last:border-0" data-testid={`order-line-${l.stock_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-ink">{l.name}{l.order_code && <span className="ml-2 font-mono text-[12px] text-ink-faint">{l.order_code}</span>}</div>
                    {draft && s && <div className="text-[12px] text-ink-faint">Have {qty(s.quantity, s.unit)}{s.par_level ? ` · par ${qty(s.par_level, s.unit)}` : ''}{l.pack_size > 1 ? ` · packs of ${l.pack_size}` : ''}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    {draft ? (
                      <div className="flex h-9 w-36 items-center overflow-hidden rounded-md border border-hairline-strong bg-white">
                        <button type="button" className="h-full w-8 bg-surface-2 text-ink-soft hover:bg-hover-tint" onClick={() => step(l, -1)} aria-label={`Less ${l.name}`}>−</button>
                        <span className="flex-1 text-center font-mono tabular-nums">{qty(l.quantity, l.unit)}</span>
                        <button type="button" className="h-full w-8 bg-surface-2 text-ink-soft hover:bg-hover-tint" onClick={() => step(l, 1)} aria-label={`More ${l.name}`}>+</button>
                      </div>
                    ) : <span className="font-mono tabular-nums">{qty(l.quantity, l.unit)}</span>}
                  </td>
                  {said && (
                    <td className="px-3 py-2.5">
                      <span className="font-mono tabular-nums">{qty(l.confirmed ?? l.quantity, l.unit)}</span>
                      {(l.confirmed ?? l.quantity) < l.quantity && <Signal tone="warn" dot={false} className="ml-2">{qty(l.quantity - l.confirmed, l.unit)} short</Signal>}
                    </td>
                  )}
                  {order.received_at && (
                    <td className="px-3 py-2.5">
                      <span className="font-mono tabular-nums">{qty(c?.received ?? 0, l.unit)}</span>
                      {c?.state === 'short' && <Signal tone="warn" dot={false} className="ml-2">short</Signal>}
                      {c?.note && <div className="text-[12px] text-ink-faint">{c.note}</div>}
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-right">
                    {draft ? (
                      <div className="relative ml-auto w-24"><span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint">£</span>
                        <Input type="number" step="0.01" min="0" value={l.price} onChange={(e) => setLine(l.stock_id, { price: Number(e.target.value) || 0 })} className="h-9 pl-6 text-right font-mono tabular-nums" aria-label={`${l.name} price`} /></div>
                    ) : <span className="font-mono tabular-nums">{money(l.price)}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(l.quantity * l.price)}</td>
                  <td className="pr-3 text-right">{draft && <button type="button" className="rounded p-1 text-ink-faint hover:text-signal-stop" onClick={() => change({ lines: order.lines.filter((x) => x.stock_id !== l.stock_id) })} aria-label={`Take ${l.name} off`}>×</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {draft && (
          <div className="border-t border-hairline px-4 py-2.5">
            {theirs.length ? (
              <Select value="" onValueChange={addLine}>
                <SelectTrigger className="h-9 w-72" data-testid="order-add"><SelectValue placeholder={`+ Add from ${whose(order.supplier_name)} products`} /></SelectTrigger>
                <SelectContent>{theirs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {money(s.cost_per_unit)}/{s.unit}</SelectItem>)}</SelectContent>
              </Select>
            ) : <span className="text-[12.5px] text-ink-faint">Everything {order.supplier_name} supplies is on the order. Give a stock item this supplier in Stock levels to add it here.</span>}
          </div>
        )}
        <div className="flex justify-end gap-6 border-t border-hairline bg-surface-2 px-4 py-3 text-[14px]">
          <span className="text-ink-soft">{order.lines.length} item{order.lines.length === 1 ? '' : 's'}</span>
          <span>{order.received_at || order.confirmed_total != null ? 'Ordered' : 'Total'} <b className="font-mono tabular-nums text-[16px]">{money(order.total)}</b></span>
          {!order.received_at && order.confirmed_total != null && order.confirmed_total !== order.total && (
            <span>They'll send <b className="font-mono tabular-nums text-[16px]" data-testid="order-confirmed-total">{money(order.confirmed_total)}</b></span>
          )}
          {order.received_at && (
            <span>Came to <b className="font-mono tabular-nums text-[16px]">{money([...(order.received_lines || []), ...(order.received_extras || [])].reduce((t, l) => t + Number(l.received ?? l.quantity) * Number(l.received_price ?? l.price ?? 0), 0))}</b></span>
          )}
        </div>
      </div>

      {draft && (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5"><span className="text-[12.5px] text-ink-soft">Deliver on</span>
            <Input type="date" value={order.deliver_on || ''} onChange={(e) => change({ deliver_on: e.target.value || null })} data-testid="order-deliver-on" /></label>
          <div className="space-y-1.5"><span className="text-[12.5px] text-ink-soft">Sends to</span>
            {supplier?.email
              ? <div className="flex h-9 items-center rounded-md border border-input bg-surface-2 px-3 text-sm text-ink">{supplier.email}</div>
              : supplier && <OrderEmailField supplierName={order.supplier_name} value={emailTo} onChange={setEmailTo} keep={keep} onKeep={setKeep} testId="order-email" />}
          </div>
          <label className="space-y-1.5 sm:col-span-2"><span className="text-[12.5px] text-ink-soft">Note for the supplier</span>
            <Input value={order.note || ''} onChange={(e) => change({ note: e.target.value })} placeholder="e.g. Back door, before 10:00, please" data-testid="order-note" /></label>
        </div>
      )}
      {!draft && order.note && <p className="text-[13px] text-ink-soft">Note: {order.note}</p>}

      {draft && (
        <div className="flex items-center justify-between">
          <button type="button" className="text-[14px] font-semibold text-signal-stop hover:underline" onClick={remove} data-testid="order-delete">Delete draft</button>
          <div className="flex items-center gap-3">
            <span className="text-[12.5px] text-ink-faint">{saved ? 'Saved' : 'Saving…'}</span>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={send} disabled={sending || !order.lines.length} data-testid="order-send">
              {sending ? 'Sending…' : `Send to ${order.supplier_name} · ${money(order.total)}`}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!cantEmail} onOpenChange={(o) => { if (!o) setCantEmail(null); }}>
        <DialogContent className="max-w-sm" data-testid="order-cant-email">
          <DialogHeader>
            <DialogTitle>It can't be emailed</DialogTitle>
            <DialogDescription>{cantEmail} Send them the PDF yourself, or phone it through, then mark it sent.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={downloadPdf}>Download PDF</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={markSent} data-testid="order-mark-sent">Mark as sent without emailing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirming && <ConfirmOrderDialog order={order} onClose={() => setConfirming(false)} onDone={(o) => { setOrder(o); setConfirming(false); }} />}

      <Dialog open={bundleName !== null} onOpenChange={(o) => { if (!o) setBundleName(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Save as a bundle</DialogTitle><DialogDescription>This order's items and quantities, to order again from {order.supplier_name} in one tap.</DialogDescription></DialogHeader>
          <Input value={bundleName || ''} onChange={(e) => setBundleName(e.target.value)} maxLength={80} data-testid="order-bundle-name" />
          {!order.lines.length && <Notice tone="warn" word="Empty">Add something to the order first.</Notice>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBundleName(null)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={saveBundle} disabled={!bundleName?.trim() || !order.lines.length} data-testid="order-bundle-save">Save the bundle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
